import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, BookOpen, ClipboardList, X, CalendarDays, Users, Download, KeyRound, Inbox, Mail, Phone , UserRound, Newspaper, Send, Gamepad2, Sparkles, Ticket, BadgeCheck, TrendingUp, HeartHandshake, Megaphone, FileText } from "lucide-react";
import MentorshipAdmin from "../components/admin/MentorshipAdmin";
import OrganizationsAdmin from "../components/admin/OrganizationsAdmin";
import OffersAdmin from "../components/admin/OffersAdmin";
import AssignmentsAdmin from "../components/admin/AssignmentsAdmin";
import VideoUploader from "../components/admin/VideoUploader";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";

const emptyCourseForm = {
  title: "", description: "", category: "", level: "Beginner", thumbnail: "",
  price: 0, duration_months: 7, start_date: "", access_code: "", schedule: "", deposit: 200, installments: 7,
};
const emptyQuestion = () => ({ question: "", options: ["", "", "", ""], correct_index: 0 });
const emptyLessonForm = { title: "", content: "", video_url: "", unlock_week: 1, quiz: [] };
const emptyInstructorForm = { name: "", title: "", bio: "", photo: "", order: 0, course_ids: [] };
const emptySessionForm = { title: "", description: "", starts_at: "", duration_min: 90, meeting_url: "", recording_url: "" };
const emptyArticleForm = { title: "", excerpt: "", content: "", cover_image: "", author: "", published: false };
const emptyGameForm = { type: "mcq", prompt: "", optionsText: "", correct_index: 0, answer: true, back: "", stepsText: "", explanation: "", category: "", active: true };

const Label = ({ children }) => (
  <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#64748B]">{children}</label>
);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtDateTime = (value) =>
  value ? new Date(value).toLocaleString("el-GR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

export default function Admin() {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("programs");
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(null); // {course, lessons}
  const [courseForm, setCourseForm] = useState(null);
  const [lessonForm, setLessonForm] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionForm, setSessionForm] = useState(null);
  const [students, setStudents] = useState(null);
  const [leads, setLeads] = useState(null);
  const [instructors, setInstructors] = useState(null);
  const [instructorForm, setInstructorForm] = useState(null);
  const [diplomas, setDiplomas] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);
  const [articles, setArticles] = useState(null);
  const [articleForm, setArticleForm] = useState(null);
  const [subscribers, setSubscribers] = useState(null);
  const [gameItems, setGameItems] = useState(null);
  const [gameForm, setGameForm] = useState(null);
  const [learnCourses, setLearnCourses] = useState(null);
  const [learnCourseForm, setLearnCourseForm] = useState(null);
  const [openLearnCourse, setOpenLearnCourse] = useState(null);
  const [learnLessons, setLearnLessons] = useState([]);
  const [learnLessonForm, setLearnLessonForm] = useState(null);
  const [coupons, setCoupons] = useState(null);
  const [couponForm, setCouponForm] = useState(null);
  const [sales, setSales] = useState(null);
  const [freeMode, setFreeMode] = useState(null);
  useEffect(() => { api.get("/settings/free-access").then((r) => setFreeMode(r.data.enabled)).catch(() => setFreeMode(false)); }, []);
  const toggleFreeMode = async () => {
    const { data } = await api.put("/admin/settings/free-access", { enabled: !freeMode });
    setFreeMode(data.enabled);
    toast.success(data.enabled ? "Όλα τα online μαθήματα είναι ΔΩΡΕΑΝ (δοκιμαστικά)" : "Οι πληρωμές ενεργοποιήθηκαν ξανά");
  };

  const reloadSales = () => api.get("/admin/academy/sales").then((r) => setSales(r.data)).catch(() => setSales(null));

  const reloadCoupons = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => setCoupons([]));

  const saveCoupon = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", {
        ...couponForm,
        value: Number(couponForm.value) || 0,
        max_uses: Number(couponForm.max_uses) || 0,
      });
      toast.success(t("admin.coupons.saved"));
      setCouponForm(null);
      await reloadCoupons();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCoupon = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.id}`, { active: !c.active });
      await reloadCoupons();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/coupons/${id}`);
      await reloadCoupons();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reloadLearnCourses = () => api.get("/admin/academy/courses").then((r) => setLearnCourses(r.data)).catch(() => setLearnCourses([]));
  const reloadLearnLessons = (courseId) =>
    api.get("/admin/academy/lessons", { params: { course_id: courseId } }).then((r) => setLearnLessons(r.data)).catch(() => setLearnLessons([]));

  const saveLearnCourse = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = learnCourseForm;
      const payload = {
        ...fields,
        hours: Number(fields.hours) || 0,
        price: Number(fields.price) || 0,
        price_original: Number(fields.price_original) || 0,
        order: Number(fields.order) || 0,
      };
      if (editingId) await api.put(`/admin/academy/courses/${editingId}`, payload);
      else await api.post("/admin/academy/courses", payload);
      toast.success(t("admin.saved.course"));
      setLearnCourseForm(null);
      await reloadLearnCourses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteLearnCourse = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/academy/courses/${id}`);
      if (openLearnCourse === id) setOpenLearnCourse(null);
      await reloadLearnCourses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveLearnLesson = async (e) => {
    e.preventDefault();
    let blocks;
    try {
      blocks = JSON.parse(learnLessonForm.blocksText || "[]");
      if (!Array.isArray(blocks)) throw new Error("array");
    } catch {
      toast.error("Blocks JSON: μη έγκυρη μορφή (χρειάζεται λίστα)");
      return;
    }
    try {
      const payload = {
        course_id: learnLessonForm.course_id,
        title: learnLessonForm.title,
        summary: learnLessonForm.summary,
        order: Number(learnLessonForm.order) || 0,
        xp: Number(learnLessonForm.xp) || 30,
        is_preview: !!learnLessonForm.is_preview,
        slides: blocks,
      };
      if (learnLessonForm.editingId) await api.put(`/admin/academy/lessons/${learnLessonForm.editingId}`, payload);
      else await api.post("/admin/academy/lessons", payload);
      toast.success(t("admin.saved.course"));
      setLearnLessonForm(null);
      await reloadLearnLessons(learnLessonForm.course_id);
      await reloadLearnCourses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteLearnLesson = async (lesson) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/academy/lessons/${lesson.id}`);
      await reloadLearnLessons(lesson.course_id);
      await reloadLearnCourses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reloadGameItems = () => api.get("/admin/game-items").then((r) => setGameItems(r.data)).catch(() => setGameItems([]));

  const saveGameItem = async (e) => {
    e.preventDefault();
    try {
      const { editingId, optionsText, stepsText, ...fields } = gameForm;
      const payload = {
        ...fields,
        correct_index: Number(fields.correct_index) || 0,
        options: optionsText.split("\n").map((s) => s.trim()).filter(Boolean),
        steps: stepsText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) await api.put(`/admin/game-items/${editingId}`, payload);
      else await api.post("/admin/game-items", payload);
      toast.success(t("admin.saved.course"));
      setGameForm(null);
      await reloadGameItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteGameItem = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/game-items/${id}`);
      await reloadGameItems();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reloadArticles = () => api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  const reloadSubscribers = () => api.get("/admin/subscribers").then((r) => setSubscribers(r.data)).catch(() => setSubscribers([]));

  const saveArticle = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = articleForm;
      if (editingId) await api.put(`/admin/articles/${editingId}`, fields);
      else await api.post("/admin/articles", fields);
      toast.success(t("admin.saved.course"));
      setArticleForm(null);
      await reloadArticles();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      await reloadArticles();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendArticleNewsletter = async (id) => {
    try {
      await api.post(`/admin/articles/${id}/newsletter`);
      toast.success(t("admin.articles.sent"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      await reloadSubscribers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadCourses = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
    return data;
  };

  const loadSelected = async (courseId) => {
    const { data } = await api.get(`/courses/${courseId}/lessons`);
    const all = await loadCourses();
    const course = all.find((c) => c.id === courseId);
    setSelected({ course, lessons: data });
    const [s, st] = await Promise.all([
      api.get(`/courses/${courseId}/sessions`).then((r) => r.data).catch(() => []),
      api.get(`/admin/courses/${courseId}/students`).then((r) => r.data).catch(() => []),
    ]);
    setSessions(s);
    setCourseStudents(st);
  };

  useEffect(() => {
    loadCourses().catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (tab === "students" && students === null) {
      api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => setStudents([]));
    }
    if (tab === "instructors" && instructors === null) {
      api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => setInstructors([]));
      api.get("/courses").then((r) => setDiplomas(r.data.filter((c) => c.mode === "live"))).catch(() => setDiplomas([]));
    }
    if (tab === "leads" && leads === null) {
      api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
    }
    if (tab === "articles" && articles === null) reloadArticles();
    if (tab === "subscribers" && subscribers === null) reloadSubscribers();
    if (tab === "games" && gameItems === null) reloadGameItems();
    if (tab === "learn" && learnCourses === null) reloadLearnCourses();
    if (tab === "coupons" && coupons === null) reloadCoupons();
    if (tab === "sales" && sales === null) reloadSales();
  }, [tab, students, leads, instructors, articles, subscribers, gameItems, learnCourses, coupons, sales]);

  const reloadInstructors = () => api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => {});

  const saveInstructor = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = instructorForm;
      const payload = { ...fields, order: Number(fields.order) || 0, course_ids: fields.course_ids || [] };
      if (editingId) await api.put(`/instructors/${editingId}`, payload);
      else await api.post("/instructors", payload);
      toast.success(t("admin.saved.course"));
      setInstructorForm(null);
      await reloadInstructors();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteInstructor = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/instructors/${id}`);
      await reloadInstructors();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reloadLeads = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});

  const toggleLead = async (id) => {
    try {
      await api.put(`/admin/leads/${id}`);
      await reloadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      await reloadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportLeadsCsv = () => {
    const rows = [["Name", "Email", "Phone", "Program", "Message", "Status", "Date"]];
    (leads || []).forEach((l) => rows.push([l.name, l.email, l.phone, l.program, l.message, l.status, l.created_at]));
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sportscience-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectCourse = async (courseId) => {
    setLessonForm(null);
    setCourseForm(null);
    setSessionForm(null);
    await loadSelected(courseId);
  };

  const saveCourse = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = courseForm;
      const payload = {
        ...fields,
        price: Number(fields.price) || 0,
        duration_months: Number(fields.duration_months) || 7,
        deposit: Number(fields.deposit) || 0,
        installments: Number(fields.installments) || 0,
      };
      if (editingId) {
        await api.put(`/courses/${editingId}`, payload);
      } else {
        await api.post("/courses", payload);
      }
      toast.success(t("admin.saved.course"));
      setCourseForm(null);
      await loadCourses();
      if (editingId) await loadSelected(editingId);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success(t("admin.deleted.course"));
      setSelected(null);
      await loadCourses();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveLesson = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = lessonForm;
      const quiz = fields.quiz.filter((q) => q.question.trim() && q.options.every((o) => o.trim()));
      const payload = { ...fields, unlock_week: Number(fields.unlock_week) || 1, quiz };
      if (editingId) {
        await api.put(`/lessons/${editingId}`, payload);
      } else {
        await api.post(`/courses/${selected.course.id}/lessons`, payload);
      }
      toast.success(t("admin.saved.lesson"));
      setLessonForm(null);
      await loadSelected(selected.course.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/lessons/${lessonId}`);
      toast.success(t("admin.deleted.lesson"));
      await loadSelected(selected.course.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSession = async (e) => {
    e.preventDefault();
    try {
      const { editingId, ...fields } = sessionForm;
      const payload = {
        ...fields,
        duration_min: Number(fields.duration_min) || 90,
        starts_at: new Date(fields.starts_at).toISOString(),
      };
      if (editingId) {
        await api.put(`/sessions/${editingId}`, payload);
      } else {
        await api.post(`/courses/${selected.course.id}/sessions`, payload);
      }
      toast.success(t("admin.saved.lesson"));
      setSessionForm(null);
      await loadSelected(selected.course.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm(t("admin.delete") + "?")) return;
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success(t("admin.deleted.lesson"));
      await loadSelected(selected.course.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateQuestion = (qi, patch) => {
    const quiz = lessonForm.quiz.map((q, i) => (i === qi ? { ...q, ...patch } : q));
    setLessonForm({ ...lessonForm, quiz });
  };

  const updateOption = (qi, oi, value) => {
    const q = lessonForm.quiz[qi];
    const options = q.options.map((o, i) => (i === oi ? value : o));
    updateQuestion(qi, { options });
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "XP", "Level", "Quiz avg", "Attempts", "Programs", "Last activity"]];
    (students || []).forEach((s) => {
      rows.push([
        s.name,
        s.email,
        s.xp,
        s.level,
        `${s.avg_score}%`,
        s.attempts,
        s.programs.map((p) => `${p.title} (${p.progress}%)`).join(" | "),
        s.last_activity || "-",
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sportscience-students.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabBtn = (key, label, Icon) => (
    <button
      data-testid={`admin-tab-${key}`}
      onClick={() => setTab(key)}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.14em] border transition-colors duration-200 ${
        tab === key ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-transparent text-[#334155] border-[#E2E8F0] hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  const courseList = (
    <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]">
      {courses.map((c) => (
        <button
          key={c.id}
          data-testid={`admin-course-item-${c.id}`}
          onClick={() => selectCourse(c.id)}
          className={`w-full text-left p-4 flex items-center gap-4 transition-colors duration-200 ${
            selected?.course?.id === c.id ? "bg-[#1D4ED8] text-white" : "text-[#0B1B3A] hover:bg-[#F8FAFC]"
          }`}
        >
          <BookOpen className="w-5 h-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{c.title}</p>
            <p className={`text-xs ${selected?.course?.id === c.id ? "text-white/80" : "text-[#64748B]"}`}>
              {c.lesson_count} {t("courses.lessons")}{c.price ? ` · ${c.price}€` : ""}
            </p>
          </div>
        </button>
      ))}
      {courses.length === 0 && <p className="p-4 text-sm text-[#94A3B8]">-</p>}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-14 pb-20" data-testid="admin-page">
      <span className="badge-lime">{t("admin.subtitle")}</span>
      <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl mt-5 text-[#0B1B3A]" data-testid="admin-title">{t("admin.title")}</h1>

      <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-tabs">
        {tabBtn("programs", t("admin.tab.programs"), BookOpen)}
        {tabBtn("sessions", t("admin.tab.sessions"), CalendarDays)}
        {tabBtn("students", t("admin.tab.students"), Users)}
        {tabBtn("leads", t("admin.tab.leads"), Inbox)}
        {tabBtn("instructors", t("admin.tab.instructors"), UserRound)}
        {tabBtn("articles", t("admin.tab.articles"), Newspaper)}
        {tabBtn("subscribers", t("admin.tab.subscribers"), Mail)}
        {/* games tab removed */}
        {tabBtn("learn", t("admin.tab.academy"), Sparkles)}
        {tabBtn("coupons", t("admin.tab.coupons"), Ticket)}
        {tabBtn("sales", t("admin.tab.sales"), TrendingUp)}
        {tabBtn("mentorship", "Mentorship", HeartHandshake)}
        {tabBtn("orgs", "B2B", HeartHandshake)}
        {tabBtn("campaigns", lang === "en" ? "Offers" : "Προσφορές", Megaphone)}
        {tabBtn("assignments", lang === "en" ? "Assignments" : "Εργασίες", FileText)}
      </div>

      {tab === "mentorship" && <MentorshipAdmin />}
      {tab === "orgs" && <OrganizationsAdmin />}
      {tab === "campaigns" && <OffersAdmin />}
      {tab === "assignments" && <AssignmentsAdmin />}

      {/* ---------------- SALES TAB ---------------- */}
      {tab === "sales" && (
        <div className="mt-10" data-testid="admin-sales-tab">
          <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.sales")}</h2>
          {sales === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6" data-testid="admin-sales-totals">
                {[
                  [t("admin.sales.total"), sales.totals.sales],
                  [t("admin.sales.revenue"), `${sales.totals.revenue}€`],
                  [t("admin.sales.pending"), sales.totals.pending],
                  [t("admin.sales.enrollments"), sales.totals.enrollments],
                  [t("admin.sales.certificates"), sales.totals.certificates],
                ].map(([label, value]) => (
                  <div key={label} className="card-dark p-5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#64748B]">{label}</p>
                    <p className="font-heading font-black text-2xl text-[#0B1B3A] mt-2">{value}</p>
                  </div>
                ))}
              </div>

              <h3 className="font-heading font-black text-lg text-[#0B1B3A] mt-10">{t("admin.sales.byCourse")}</h3>
              {sales.courses.length === 0 ? (
                <div className="card-dark p-10 mt-4 text-center text-[#64748B]" data-testid="admin-sales-empty">{t("admin.sales.empty")}</div>
              ) : (
                <div className="mt-4 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-sales-courses">
                  {sales.courses.map((c) => (
                    <div key={c.slug} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-sales-course-${c.slug}`}>
                      <p className="flex-1 min-w-[200px] font-bold text-[#0B1B3A]">{c.title}</p>
                      <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]">{c.sales} × </span>
                      <span className="font-heading font-black text-lg text-[#1D4ED8]">{c.revenue}€</span>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="font-heading font-black text-lg text-[#0B1B3A] mt-10">{t("admin.sales.byCoupon")}</h3>
              <div className="mt-4 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-sales-coupons">
                {sales.coupons.length === 0 ? (
                  <div className="p-10 text-center text-[#64748B]">{t("admin.coupons.empty")}</div>
                ) : (
                  sales.coupons.map((c) => (
                    <div key={c.code} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-sales-coupon-${c.code}`}>
                      <span className="font-heading font-black text-[#0B1B3A] tracking-wide">{c.code}</span>
                      {c.type && (
                        <span className="px-3 py-1 rounded-full bg-[#EEF4FF] text-[11px] font-bold text-[#1D4ED8]">
                          {c.type === "percent" ? `-${c.value}%` : `-${c.value}€`}
                        </span>
                      )}
                      <span className="flex-1 min-w-[140px] text-xs text-[#64748B]">
                        {t("admin.sales.total")}: {c.sales} · {t("admin.coupons.used")}: {c.used ?? 0}
                      </span>
                      <span className="font-heading font-black text-lg text-[#1D4ED8]">{c.revenue}€</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------------- COUPONS TAB ---------------- */}
      {tab === "coupons" && (
        <div className="mt-10" data-testid="admin-coupons-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.coupons")}</h2>
            <button data-testid="admin-new-coupon-button" onClick={() => setCouponForm({ code: "", type: "percent", value: 20, expires_at: "", max_uses: 0, active: true })} className="btn-blue !px-5 !py-2 text-xs">
              <Plus className="w-4 h-4" /> {t("admin.newCoupon")}
            </button>
          </div>

          {coupons === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : coupons.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-coupons-empty">{t("admin.coupons.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-coupons-list">
              {coupons.map((c) => (
                <div key={c.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-coupon-row-${c.code}`}>
                  <span className="font-heading font-black text-[#0B1B3A] tracking-wide">{c.code}</span>
                  <span className="px-3 py-1 rounded-full bg-[#EEF4FF] text-[11px] font-bold text-[#1D4ED8]">
                    {c.type === "percent" ? `-${c.value}%` : `-${c.value}€`}
                  </span>
                  <span className="text-xs text-[#64748B] flex-1 min-w-[160px]">
                    {t("admin.coupons.used")}: {c.used || 0}{c.max_uses ? `/${c.max_uses}` : ""}
                    {c.expires_at ? ` · ${t("admin.coupons.expires")}: ${c.expires_at}` : ""}
                  </span>
                  <button data-testid={`admin-toggle-coupon-${c.code}`} onClick={() => toggleCoupon(c)} className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors duration-200 ${c.active ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-transparent text-[#475569] border-[#E2E8F0]"}`}>
                    {c.active ? t("admin.coupons.active") : t("admin.coupons.inactive")}
                  </button>
                  <button data-testid={`admin-delete-coupon-${c.code}`} onClick={() => deleteCoupon(c.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {couponForm && (
            <form onSubmit={saveCoupon} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-coupon-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newCoupon")}</h4>
                <button type="button" data-testid="admin-coupon-cancel" onClick={() => setCouponForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.couponCode")}</Label>
                  <input data-testid="admin-coupon-code-input" required value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} className="input-flat mt-2" placeholder="SEMINAR20" />
                </div>
                <div>
                  <Label>{t("admin.field.couponType")}</Label>
                  <select data-testid="admin-coupon-type-select" value={couponForm.type} onChange={(e) => setCouponForm({ ...couponForm, type: e.target.value })} className="input-flat mt-2">
                    <option value="percent">{t("admin.field.couponPercent")}</option>
                    <option value="amount">{t("admin.field.couponAmount")}</option>
                  </select>
                </div>
                <div>
                  <Label>{t("admin.field.couponValue")}</Label>
                  <input data-testid="admin-coupon-value-input" type="number" min={1} value={couponForm.value} onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.couponMaxUses")}</Label>
                  <input data-testid="admin-coupon-maxuses-input" type="number" min={0} value={couponForm.max_uses} onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.couponExpires")}</Label>
                  <input data-testid="admin-coupon-expires-input" type="date" value={couponForm.expires_at} onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <button data-testid="admin-coupon-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}
        </div>
      )}

      {/* ---------------- INTERACTIVE (LEARN) TAB ---------------- */}
      {tab === "learn" && (
        <div className="mt-10" data-testid="admin-learn-tab">
          <div className={`p-5 mb-8 rounded-[22px] border flex flex-wrap items-center gap-4 ${freeMode ? "bg-[#FEF3C7] border-[#FCD34D]" : "bg-[#EEF4FF] border-[#BFDBFE]"}`} data-testid="admin-free-mode-panel">
            <div className="flex-1 min-w-[240px]">
              <p className="font-heading font-black text-[#0B1B3A]">Δοκιμαστική λειτουργία: όλα τα online μαθήματα δωρεάν</p>
              <p className="text-xs text-[#475569] mt-1">{freeMode ? "ΕΝΕΡΓΗ — κανείς δεν πληρώνει. Απενεργοποίησέ την πριν τη δημοσίευση." : "Ανενεργή — οι μαθητές πληρώνουν κανονικά."}</p>
            </div>
            <button data-testid="admin-free-mode-toggle" onClick={toggleFreeMode} disabled={freeMode === null} className={freeMode ? "btn-blue" : "btn-outline-light"}>
              {freeMode ? "Απενεργοποίηση (επανέφερε πληρωμές)" : "Ενεργοποίηση δωρεάν πρόσβασης"}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.academy")}</h2>
            <button data-testid="admin-new-learn-course-button" onClick={() => setLearnCourseForm({ editingId: null, slug: "", title: "", subtitle: "", description: "", category: "Επαγγελματικά", level: "Αρχάριο", hours: 3, price: 39, price_original: 120, lookup_key: "", outcomes: [], final_quiz: [], order: 0, active: true })} className="btn-blue !px-5 !py-2 text-xs">
              <Plus className="w-4 h-4" /> {t("admin.newAcademyCourse")}
            </button>
          </div>

          {learnCourses === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : learnCourses.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-learn-empty">{t("admin.academy.empty")}</div>
          ) : (
            <div className="mt-6 space-y-3" data-testid="admin-learn-course-list">
              {learnCourses.map((c) => (
                <div key={c.id} className="card-dark p-5" data-testid={`admin-learn-course-row-${c.id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-bold text-[#0B1B3A]">{c.title}</p>
                      <p className="text-xs text-[#64748B] mt-1">{c.lessons_count} {t("learn.lessons")}</p>
                    </div>
                    <button
                      data-testid={`admin-learn-course-open-${c.id}`}
                      onClick={() => {
                        const next = openLearnCourse === c.id ? null : c.id;
                        setOpenLearnCourse(next);
                        setLearnLessonForm(null);
                        if (next) reloadLearnLessons(next);
                      }}
                      className="btn-outline-light !px-5 !py-2 text-xs"
                    >
                      {t("admin.learn.lessonsFor")}
                    </button>
                    <button data-testid={`admin-edit-learn-course-${c.id}`} onClick={() => setLearnCourseForm({ editingId: c.id, slug: c.slug || "", title: c.title, subtitle: c.subtitle || "", description: c.description || "", category: c.category || "", level: c.level || "", hours: c.hours || 0, price: c.price || 0, price_original: c.price_original || 0, lookup_key: c.lookup_key || "", outcomes: c.outcomes || [], final_quiz: c.final_quiz || [], order: c.order || 0, active: c.active !== false })} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`admin-delete-learn-course-${c.id}`} onClick={() => deleteLearnCourse(c.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {openLearnCourse === c.id && (
                    <div className="mt-5 pt-5 border-t border-[#E2E8F0]" data-testid={`admin-learn-lessons-${c.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B]">{t("admin.learn.lessonsFor")}</p>
                        <button data-testid={`admin-new-learn-lesson-${c.id}`} onClick={() => setLearnLessonForm({ editingId: null, course_id: c.id, title: "", summary: "", order: learnLessons.length + 1, xp: 25, is_preview: false, blocksText: "[\n  {\n    \"type\": \"slide\",\n    \"title\": \"\",\n    \"bullets\": [],\n    \"note\": \"\"\n  }\n]" })} className="btn-blue !px-4 !py-1.5 text-[11px]">
                          <Plus className="w-3.5 h-3.5" /> {t("admin.newAcademyLesson")}
                        </button>
                      </div>
                      <div className="mt-4 divide-y divide-[#E2E8F0]">
                        {learnLessons.map((l) => (
                          <div key={l.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`admin-learn-lesson-row-${l.id}`}>
                            <p className="flex-1 min-w-[200px] text-sm font-semibold text-[#0B1B3A]">{l.order}. {l.title}</p>
                            <span className="text-xs text-[#64748B]">{(l.slides || []).length} slides · {l.xp} XP{l.is_preview ? " · preview" : ""}</span>
                            <button data-testid={`admin-edit-learn-lesson-${l.id}`} onClick={() => setLearnLessonForm({ editingId: l.id, course_id: c.id, title: l.title, summary: l.summary || "", order: l.order || 0, xp: l.xp || 25, is_preview: !!l.is_preview, blocksText: JSON.stringify(l.slides || [], null, 2) })} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`admin-delete-learn-lesson-${l.id}`} onClick={() => deleteLearnLesson({ ...l, course_id: c.id })} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {learnCourseForm && (
            <form onSubmit={saveLearnCourse} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-learn-course-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newAcademyCourse")}</h4>
                <button type="button" data-testid="admin-learn-course-cancel" onClick={() => setLearnCourseForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.title")}</Label>
                  <input data-testid="admin-learn-course-title-input" required value={learnCourseForm.title} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, title: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.learnSubtitle")}</Label>
                  <input data-testid="admin-learn-course-subtitle-input" value={learnCourseForm.subtitle} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, subtitle: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                <div>
                  <Label>{t("admin.field.academySlug")}</Label>
                  <input data-testid="admin-learn-course-slug-input" required value={learnCourseForm.slug} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, slug: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.academyCategory")}</Label>
                  <input data-testid="admin-learn-course-category-input" value={learnCourseForm.category} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, category: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.academyPrice")}</Label>
                  <input data-testid="admin-learn-course-price-input" type="number" min={0} value={learnCourseForm.price} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, price: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.academyOriginal")}</Label>
                  <input data-testid="admin-learn-course-original-input" type="number" min={0} value={learnCourseForm.price_original} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, price_original: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.academyLevel")}</Label>
                  <input data-testid="admin-learn-course-level-input" value={learnCourseForm.level} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, level: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.academyHours")}</Label>
                  <input data-testid="admin-learn-course-hours-input" type="number" min={0} value={learnCourseForm.hours} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, hours: e.target.value })} className="input-flat mt-2" />
                </div>
                <div className="col-span-2">
                  <Label>{t("admin.field.academyLookup")}</Label>
                  <input data-testid="admin-learn-course-lookup-input" value={learnCourseForm.lookup_key} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, lookup_key: e.target.value })} className="input-flat mt-2" placeholder="academy_..." />
                </div>
              </div>
              <div>
                <Label>{t("admin.field.description")}</Label>
                <textarea data-testid="admin-learn-course-description-input" rows={3} value={learnCourseForm.description} onChange={(e) => setLearnCourseForm({ ...learnCourseForm, description: e.target.value })} className="input-flat mt-2" />
              </div>
              <button data-testid="admin-learn-course-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}

          {learnLessonForm && (
            <form onSubmit={saveLearnLesson} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-learn-lesson-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newAcademyLesson")}</h4>
                <button type="button" data-testid="admin-learn-lesson-cancel" onClick={() => setLearnLessonForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2">
                  <Label>{t("admin.field.title")}</Label>
                  <input data-testid="admin-learn-lesson-title-input" required value={learnLessonForm.title} onChange={(e) => setLearnLessonForm({ ...learnLessonForm, title: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.learnXp")}</Label>
                  <input data-testid="admin-learn-lesson-xp-input" type="number" min={0} value={learnLessonForm.xp} onChange={(e) => setLearnLessonForm({ ...learnLessonForm, xp: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <div>
                <Label>{t("admin.field.description")}</Label>
                <input data-testid="admin-learn-lesson-summary-input" value={learnLessonForm.summary} onChange={(e) => setLearnLessonForm({ ...learnLessonForm, summary: e.target.value })} className="input-flat mt-2" />
              </div>
              <label className="flex items-center gap-3 text-sm font-semibold text-[#334155]">
                <input data-testid="admin-learn-lesson-preview-checkbox" type="checkbox" checked={!!learnLessonForm.is_preview} onChange={(e) => setLearnLessonForm({ ...learnLessonForm, is_preview: e.target.checked })} className="w-4 h-4 accent-[#1D4ED8]" />
                {t("admin.field.academyPreview")}
              </label>
              <div>
                <Label>{t("admin.field.academySlides")}</Label>
                <p className="text-xs text-[#64748B] mt-1">{t("admin.academy.slidesHelp")}</p>
                <div className="mt-3">
                  <VideoUploader onUploaded={(v) => {
                    let blocks = [];
                    try { blocks = JSON.parse(learnLessonForm.blocksText || "[]"); } catch { blocks = []; }
                    blocks.push({ type: "video", title: v.filename.replace(/\.[^.]+$/, ""), url: v.url, note: "" });
                    setLearnLessonForm({ ...learnLessonForm, blocksText: JSON.stringify(blocks, null, 2) });
                  }} />
                </div>
                <textarea data-testid="admin-learn-lesson-blocks-input" rows={14} value={learnLessonForm.blocksText} onChange={(e) => setLearnLessonForm({ ...learnLessonForm, blocksText: e.target.value })} className="input-flat mt-2 font-mono text-xs" />
              </div>
              <button data-testid="admin-learn-lesson-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}
        </div>
      )}

      {/* ---------------- GAMES TAB ---------------- */}
      {tab === "games" && (
        <div className="mt-10" data-testid="admin-games-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.games")}</h2>
            <button data-testid="admin-new-game-item-button" onClick={() => setGameForm({ editingId: null, ...emptyGameForm })} className="btn-blue !px-5 !py-2 text-xs">
              <Plus className="w-4 h-4" /> {t("admin.newGameItem")}
            </button>
          </div>

          {gameItems === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : gameItems.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-games-empty">{t("admin.games.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-game-items-list">
              {gameItems.map((g) => (
                <div key={g.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-game-item-row-${g.id}`}>
                  <span className="px-3 py-1 rounded-full bg-[#EEF4FF] text-[11px] font-bold uppercase tracking-wider text-[#1D4ED8]">{g.type}</span>
                  <p className="flex-1 min-w-[220px] font-semibold text-[#0B1B3A]">{g.prompt}</p>
                  {g.category && <span className="text-xs text-[#64748B]">{g.category}</span>}
                  <button data-testid={`admin-edit-game-item-${g.id}`} onClick={() => setGameForm({ editingId: g.id, type: g.type, prompt: g.prompt, optionsText: (g.options || []).join("\n"), correct_index: g.correct_index || 0, answer: !!g.answer, back: g.back || "", stepsText: (g.steps || []).join("\n"), explanation: g.explanation || "", category: g.category || "", active: g.active !== false })} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-game-item-${g.id}`} onClick={() => deleteGameItem(g.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {gameForm && (
            <form onSubmit={saveGameItem} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-game-item-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newGameItem")}</h4>
                <button type="button" data-testid="admin-game-item-cancel" onClick={() => setGameForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.gameType")}</Label>
                  <select data-testid="admin-game-item-type-select" value={gameForm.type} onChange={(e) => setGameForm({ ...gameForm, type: e.target.value })} className="input-flat mt-2">
                    <option value="mcq">mcq — Quiz Rush</option>
                    <option value="tf">tf — Σωστό/Λάθος</option>
                    <option value="card">card — Flashcard</option>
                    <option value="session">session — Χτίσε την Προπόνηση</option>
                  </select>
                </div>
                <div>
                  <Label>{t("admin.field.gameCategory")}</Label>
                  <input data-testid="admin-game-item-category-input" value={gameForm.category} onChange={(e) => setGameForm({ ...gameForm, category: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <div>
                <Label>{t("admin.field.gamePrompt")}</Label>
                <textarea data-testid="admin-game-item-prompt-input" required rows={2} value={gameForm.prompt} onChange={(e) => setGameForm({ ...gameForm, prompt: e.target.value })} className="input-flat mt-2" />
              </div>
              {gameForm.type === "mcq" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="md:col-span-2">
                    <Label>{t("admin.field.gameOptions")}</Label>
                    <textarea data-testid="admin-game-item-options-input" rows={4} value={gameForm.optionsText} onChange={(e) => setGameForm({ ...gameForm, optionsText: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.gameCorrect")}</Label>
                    <input data-testid="admin-game-item-correct-input" type="number" min={0} value={gameForm.correct_index} onChange={(e) => setGameForm({ ...gameForm, correct_index: e.target.value })} className="input-flat mt-2" />
                  </div>
                </div>
              )}
              {gameForm.type === "tf" && (
                <label className="flex items-center gap-3 text-sm font-semibold text-[#334155]">
                  <input data-testid="admin-game-item-answer-checkbox" type="checkbox" checked={gameForm.answer} onChange={(e) => setGameForm({ ...gameForm, answer: e.target.checked })} className="w-4 h-4 accent-[#1D4ED8]" />
                  {t("admin.field.gameAnswer")}
                </label>
              )}
              {gameForm.type === "card" && (
                <div>
                  <Label>{t("admin.field.gameBack")}</Label>
                  <textarea data-testid="admin-game-item-back-input" rows={3} value={gameForm.back} onChange={(e) => setGameForm({ ...gameForm, back: e.target.value })} className="input-flat mt-2" />
                </div>
              )}
              {gameForm.type === "session" && (
                <div>
                  <Label>{t("admin.field.gameSteps")}</Label>
                  <textarea data-testid="admin-game-item-steps-input" rows={6} value={gameForm.stepsText} onChange={(e) => setGameForm({ ...gameForm, stepsText: e.target.value })} className="input-flat mt-2" />
                </div>
              )}
              <div>
                <Label>{t("admin.field.gameExplanation")}</Label>
                <textarea data-testid="admin-game-item-explanation-input" rows={2} value={gameForm.explanation} onChange={(e) => setGameForm({ ...gameForm, explanation: e.target.value })} className="input-flat mt-2" />
              </div>
              <button data-testid="admin-game-item-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}
        </div>
      )}

      {/* ---------------- ARTICLES TAB ---------------- */}
      {tab === "articles" && (
        <div className="mt-10" data-testid="admin-articles-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.articles")}</h2>
            <button data-testid="admin-new-article-button" onClick={() => setArticleForm({ editingId: null, ...emptyArticleForm })} className="btn-blue !px-5 !py-2 text-xs">
              <Plus className="w-4 h-4" /> {t("admin.newArticle")}
            </button>
          </div>

          {articles === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : articles.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-articles-empty">{t("admin.articles.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-articles-list">
              {articles.map((a) => (
                <div key={a.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-article-row-${a.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3A]">{a.title}</p>
                    <p className="text-xs text-[#64748B] mt-1">{fmtDateTime(a.published_at || a.created_at)}</p>
                  </div>
                  <span className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${a.published ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-transparent text-[#475569] border-[#E2E8F0]"}`}>
                    {a.published ? t("admin.articles.published") : t("admin.articles.draft")}
                  </span>
                  {a.published && (
                    <button data-testid={`admin-article-newsletter-${a.id}`} onClick={() => sendArticleNewsletter(a.id)} title={t("admin.articles.sendNewsletter")} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  <button data-testid={`admin-edit-article-${a.id}`} onClick={() => setArticleForm({ editingId: a.id, title: a.title, excerpt: a.excerpt || "", content: a.content || "", cover_image: a.cover_image || "", author: a.author || "", published: !!a.published })} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-article-${a.id}`} onClick={() => deleteArticle(a.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {articleForm && (
            <form onSubmit={saveArticle} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-article-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newArticle")}</h4>
                <button type="button" data-testid="admin-article-cancel" onClick={() => setArticleForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div>
                <Label>{t("admin.field.artTitle")}</Label>
                <input data-testid="admin-article-title-input" required value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} className="input-flat mt-2" />
              </div>
              <div>
                <Label>{t("admin.field.artExcerpt")}</Label>
                <textarea data-testid="admin-article-excerpt-input" rows={2} value={articleForm.excerpt} onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })} className="input-flat mt-2" />
              </div>
              <div>
                <Label>{t("admin.field.artContent")}</Label>
                <textarea data-testid="admin-article-content-input" rows={10} value={articleForm.content} onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })} className="input-flat mt-2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.artCover")}</Label>
                  <input data-testid="admin-article-cover-input" value={articleForm.cover_image} onChange={(e) => setArticleForm({ ...articleForm, cover_image: e.target.value })} className="input-flat mt-2" placeholder="https://..." />
                </div>
                <div>
                  <Label>{t("admin.field.artAuthor")}</Label>
                  <input data-testid="admin-article-author-input" value={articleForm.author} onChange={(e) => setArticleForm({ ...articleForm, author: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm font-semibold text-[#334155]">
                <input data-testid="admin-article-published-checkbox" type="checkbox" checked={articleForm.published} onChange={(e) => setArticleForm({ ...articleForm, published: e.target.checked })} className="w-4 h-4 accent-[#1D4ED8]" />
                {t("admin.field.artPublished")}
              </label>
              <button data-testid="admin-article-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}
        </div>
      )}

      {/* ---------------- SUBSCRIBERS TAB ---------------- */}
      {tab === "subscribers" && (
        <div className="mt-10" data-testid="admin-subscribers-tab">
          <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.subscribers")}</h2>
          {subscribers === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : subscribers.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-subscribers-empty">{t("admin.subscribers.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-subscribers-list">
              {subscribers.map((s) => (
                <div key={s.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-subscriber-row-${s.id}`}>
                  <span className="inline-flex items-center gap-2 flex-1 min-w-[220px] font-bold text-[#0B1B3A]">
                    <Mail className="w-4 h-4 text-[#1D4ED8]" /> {s.email}
                  </span>
                  <span className="text-xs text-[#94A3B8]">{fmtDateTime(s.created_at)}</span>
                  {!s.active && <span className="text-[11px] font-bold uppercase tracking-wider text-[#EF4444]">unsubscribed</span>}
                  <button data-testid={`admin-delete-subscriber-${s.id}`} onClick={() => deleteSubscriber(s.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- STUDENTS TAB ---------------- */}
      {tab === "students" && (
        <div className="mt-10" data-testid="admin-students-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.students")}</h2>
            <button data-testid="admin-export-csv" onClick={exportCsv} className="btn-blue !px-5 !py-2 text-xs">
              <Download className="w-4 h-4" /> {t("admin.exportCsv")}
            </button>
          </div>
          {students === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : students.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-students-empty">{t("admin.noStudents")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-students-list">
              {students.map((s) => (
                <div key={s.id} className="p-5" data-testid={`admin-student-row-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="w-10 h-10 rounded-full bg-[#1D4ED8] text-white font-heading font-black flex items-center justify-center">
                      {s.name.charAt(0)}
                    </span>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-bold text-[#0B1B3A]">{s.name}</p>
                      <p className="text-xs text-[#64748B]">{s.email}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-heading font-black text-xl text-[#1D4ED8]">{s.xp}</p>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">XP · {s.level}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-heading font-black text-xl text-[#0B1B3A]">{s.avg_score}%</p>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#94A3B8]">{t("admin.students.avg")}</p>
                    </div>
                    <div className="text-right min-w-[140px]">
                      <p className="text-xs text-[#64748B]">{t("admin.lastActivity")}</p>
                      <p className="text-xs font-semibold text-[#334155]">{s.last_activity ? fmtDateTime(s.last_activity) : "-"}</p>
                    </div>
                  </div>
                  {s.programs.length > 0 && (
                    <div className="mt-4 pl-14 space-y-2">
                      {s.programs.map((p) => (
                        <div key={p.course_id} className="flex items-center gap-3">
                          <span className="text-xs text-[#334155] flex-1 min-w-0 truncate">{p.title}</span>
                          <div className="h-1.5 w-28 bg-[#F1F5F9] rounded-full overflow-hidden">
                            <div className="h-full bg-[#1D4ED8]" style={{ width: `${p.progress}%` }} />
                          </div>
                          <span className="text-xs font-bold text-[#1D4ED8] w-12 text-right">{p.progress}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- INSTRUCTORS TAB ---------------- */}
      {tab === "instructors" && (
        <div className="mt-10" data-testid="admin-instructors-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.instructors")}</h2>
            <button data-testid="admin-new-instructor-button" onClick={() => setInstructorForm({ editingId: null, ...emptyInstructorForm })} className="btn-blue !px-5 !py-2 text-xs">
              <Plus className="w-4 h-4" /> {t("admin.newInstructor")}
            </button>
          </div>

          {instructors === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : instructors.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-instructors-empty">{t("admin.instructors.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-instructors-list">
              {instructors.map((p) => (
                <div key={p.id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`admin-instructor-row-${p.id}`}>
                  <span className="w-11 h-11 rounded-full bg-[#EEF4FF] overflow-hidden flex items-center justify-center">
                    {p.photo ? <img src={p.photo} alt={p.name} className="w-full h-full object-cover" /> : <UserRound className="w-5 h-5 text-[#1D4ED8]" />}
                  </span>
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-bold text-[#0B1B3A]">{p.name}</p>
                    <p className="text-xs text-[#64748B]">{p.title}</p>
                  </div>
                  <button data-testid={`admin-edit-instructor-${p.id}`} onClick={() => setInstructorForm({ editingId: p.id, name: p.name, title: p.title || "", bio: p.bio || "", photo: p.photo || "", order: p.order || 0, course_ids: p.course_ids || [] })} className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`admin-delete-instructor-${p.id}`} onClick={() => deleteInstructor(p.id)} className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-white transition-colors duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {instructorForm && (
            <form onSubmit={saveInstructor} className="card-dark p-7 mt-8 space-y-5" data-testid="admin-instructor-form">
              <div className="flex items-center justify-between">
                <h4 className="font-heading font-black text-lg text-[#0B1B3A]">{t("admin.newInstructor")}</h4>
                <button type="button" data-testid="admin-instructor-cancel" onClick={() => setInstructorForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] transition-colors duration-200">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.instName")}</Label>
                  <input data-testid="admin-instructor-name-input" required value={instructorForm.name} onChange={(e) => setInstructorForm({ ...instructorForm, name: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.instTitle")}</Label>
                  <input data-testid="admin-instructor-title-input" value={instructorForm.title} onChange={(e) => setInstructorForm({ ...instructorForm, title: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <div>
                <Label>{t("admin.field.instBio")}</Label>
                <textarea data-testid="admin-instructor-bio-input" rows={4} value={instructorForm.bio} onChange={(e) => setInstructorForm({ ...instructorForm, bio: e.target.value })} className="input-flat mt-2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label>{t("admin.field.instPhoto")}</Label>
                  <input data-testid="admin-instructor-photo-input" value={instructorForm.photo} onChange={(e) => setInstructorForm({ ...instructorForm, photo: e.target.value })} className="input-flat mt-2" placeholder="https://..." />
                </div>
                <div>
                  <Label>{t("admin.field.instOrder")}</Label>
                  <input data-testid="admin-instructor-order-input" type="number" value={instructorForm.order} onChange={(e) => setInstructorForm({ ...instructorForm, order: e.target.value })} className="input-flat mt-2" />
                </div>
              </div>
              <div>
                <Label>{t("admin.field.instDiplomas")}</Label>
                <div className="flex flex-wrap gap-3 mt-3" data-testid="admin-instructor-diplomas">
                  {(diplomas || []).filter((c) => c.mode === "live").map((c) => {
                    const on = (instructorForm.course_ids || []).includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`admin-instructor-diploma-${c.id}`}
                        onClick={() => setInstructorForm({
                          ...instructorForm,
                          course_ids: on
                            ? instructorForm.course_ids.filter((x) => x !== c.id)
                            : [...(instructorForm.course_ids || []), c.id],
                        })}
                        className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors duration-200 ${on ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "border-[#E2E8F0] text-[#334155] hover:border-[#1D4ED8]"}`}
                      >
                        {c.title}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button data-testid="admin-instructor-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
            </form>
          )}
        </div>
      )}

      {/* ---------------- LEADS TAB ---------------- */}
      {tab === "leads" && (
        <div className="mt-10" data-testid="admin-leads-tab">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">{t("admin.tab.leads")}</h2>
            <button data-testid="admin-export-leads-csv" onClick={exportLeadsCsv} className="btn-blue !px-5 !py-2 text-xs">
              <Download className="w-4 h-4" /> {t("admin.exportCsv")}
            </button>
          </div>
          {leads === null ? (
            <p className="text-[#64748B] mt-6">{t("common.loading")}</p>
          ) : leads.length === 0 ? (
            <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-leads-empty">{t("admin.leads.empty")}</div>
          ) : (
            <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-leads-list">
              {leads.map((l) => (
                <div key={l.id} className="p-5 flex flex-wrap items-start gap-4" data-testid={`admin-lead-row-${l.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3A]">{l.name}</p>
                    <p className="text-xs text-[#475569] mt-1 flex flex-wrap items-center gap-4">
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</span>
                      {l.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {l.phone}</span>}
                    </p>
                    {l.program && <p className="text-xs text-[#1D4ED8] mt-2 font-bold uppercase tracking-wider">{l.program}</p>}
                    {l.message && <p className="text-sm text-[#475569] mt-2 max-w-2xl">{l.message}</p>}
                  </div>
                  <span className="text-xs text-[#94A3B8]">{fmtDateTime(l.created_at)}</span>
                  <button
                    data-testid={`admin-lead-toggle-${l.id}`}
                    onClick={() => toggleLead(l.id)}
                    className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors duration-200 ${
                      l.status === "new"
                        ? "bg-[#1D4ED8] text-white border-[#1D4ED8]"
                        : "bg-transparent text-[#475569] border-[#E2E8F0]"
                    }`}
                  >
                    {l.status === "new" ? t("admin.leads.new") : t("admin.leads.contacted")}
                  </button>
                  <button
                    data-testid={`admin-lead-delete-${l.id}`}
                    onClick={() => removeLead(l.id)}
                    className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-[#1D4ED8] transition-colors duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- SESSIONS TAB ---------------- */}
      {tab === "sessions" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10" data-testid="admin-sessions-tab">
          <div className="lg:col-span-4">
            <h2 className="font-heading font-black text-xl text-[#0B1B3A]">{t("admin.courses")}</h2>
            {courseList}
          </div>
          <div className="lg:col-span-8">
            {!selected ? (
              <div className="card-dark p-16 text-center text-[#64748B]" data-testid="admin-sessions-empty-state">{t("admin.selectProgram")}</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{selected.course.title}</h3>
                  <button
                    data-testid="admin-new-session-button"
                    onClick={() => setSessionForm({ editingId: null, ...emptySessionForm })}
                    className="btn-blue !px-5 !py-2 text-xs"
                  >
                    <Plus className="w-4 h-4" /> {t("admin.newSession")}
                  </button>
                </div>

                <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]">
                  {sessions.map((s) => (
                    <div key={s.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-session-item-${s.id}`}>
                      <CalendarDays className="w-5 h-5 text-[#1D4ED8]" />
                      <div className="flex-1 min-w-[180px]">
                        <p className="font-bold text-[#0B1B3A]">{s.title}</p>
                        <p className="text-xs text-[#64748B]">{fmtDateTime(s.starts_at)} · {s.duration_min} {t("sessions.min")}</p>
                      </div>
                      <button
                        data-testid={`admin-edit-session-${s.id}`}
                        onClick={() => setSessionForm({
                          editingId: s.id,
                          title: s.title,
                          description: s.description || "",
                          starts_at: toLocalInput(s.starts_at),
                          duration_min: s.duration_min,
                          meeting_url: s.meeting_url || "",
                          recording_url: s.recording_url || "",
                        })}
                        className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-delete-session-${s.id}`}
                        onClick={() => deleteSession(s.id)}
                        className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-[#1D4ED8] transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {sessions.length === 0 && <p className="p-4 text-sm text-[#94A3B8]">{t("sessions.empty")}</p>}
                </div>

                {sessionForm && (
                  <motion.form
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={saveSession}
                    className="card-dark p-7 mt-8 space-y-5"
                    data-testid="admin-session-form"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-heading font-black text-lg text-[#0B1B3A]">
                        {sessionForm.editingId ? t("admin.editSession") : t("admin.newSession")}
                      </h4>
                      <button type="button" data-testid="admin-session-cancel" onClick={() => setSessionForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#1D4ED8] transition-colors duration-200">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div>
                      <Label>{t("admin.field.title")}</Label>
                      <input data-testid="admin-session-title-input" required value={sessionForm.title} onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })} className="input-flat mt-2" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label>{t("admin.field.when")}</Label>
                        <input data-testid="admin-session-when-input" required type="datetime-local" value={sessionForm.starts_at} onChange={(e) => setSessionForm({ ...sessionForm, starts_at: e.target.value })} className="input-flat mt-2" />
                      </div>
                      <div>
                        <Label>{t("admin.field.duration")}</Label>
                        <input data-testid="admin-session-duration-input" type="number" min={15} value={sessionForm.duration_min} onChange={(e) => setSessionForm({ ...sessionForm, duration_min: e.target.value })} className="input-flat mt-2" />
                      </div>
                    </div>
                    <div>
                      <Label>{t("admin.field.meetingUrl")}</Label>
                      <input data-testid="admin-session-url-input" value={sessionForm.meeting_url} onChange={(e) => setSessionForm({ ...sessionForm, meeting_url: e.target.value })} className="input-flat mt-2" placeholder="https://zoom.us/j/..." />
                    </div>
                    <div>
                      <Label>{t("admin.field.recordingUrl")}</Label>
                      <input data-testid="admin-session-recording-input" value={sessionForm.recording_url} onChange={(e) => setSessionForm({ ...sessionForm, recording_url: e.target.value })} className="input-flat mt-2" placeholder="https://..." />
                    </div>
                    <div>
                      <Label>{t("admin.field.description")}</Label>
                      <textarea data-testid="admin-session-description-input" rows={3} value={sessionForm.description} onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })} className="input-flat mt-2" />
                    </div>
                    <button data-testid="admin-session-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
                  </motion.form>
                )}

                {/* Students of this program */}
                <div className="mt-12">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#1D4ED8]" />
                    <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{t("admin.tab.students")}</h3>
                  </div>
                  {courseStudents.length === 0 ? (
                    <p className="text-[#64748B] mt-4" data-testid="admin-course-students-empty">{t("admin.noStudents")}</p>
                  ) : (
                    <div className="mt-4 card-dark overflow-hidden divide-y divide-[#E2E8F0]" data-testid="admin-course-students">
                      {courseStudents.map((s) => (
                        <div key={s.email} className="flex flex-wrap items-center gap-4 p-4" data-testid={`admin-course-student-${s.email}`}>
                          <div className="flex-1 min-w-[160px]">
                            <p className="font-bold text-[#0B1B3A] text-sm">{s.name}</p>
                            <p className="text-xs text-[#64748B]">{s.email}</p>
                          </div>
                          <div className="h-1.5 w-28 bg-[#F1F5F9] rounded-full overflow-hidden">
                            <div className="h-full bg-[#1D4ED8]" style={{ width: `${s.progress}%` }} />
                          </div>
                          <span className="text-sm font-bold text-[#1D4ED8] w-14 text-right">{s.progress}%</span>
                          <span className="text-xs text-[#64748B] w-24 text-right">{s.completed}/{s.total}</span>
                          <span className="text-xs text-[#64748B] w-16 text-right">{s.avg_score}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------------- PROGRAMS TAB ---------------- */}
      {tab === "programs" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
          <div className="lg:col-span-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-black text-xl text-[#0B1B3A]">{t("admin.courses")}</h2>
              <button
                data-testid="admin-new-course-button"
                onClick={() => { setCourseForm({ editingId: null, ...emptyCourseForm }); setSelected(null); setLessonForm(null); }}
                className="btn-blue !px-5 !py-2 text-xs"
              >
                <Plus className="w-4 h-4" /> {t("admin.newCourse")}
              </button>
            </div>
            {courseList}
          </div>

          <div className="lg:col-span-8">
            {courseForm && (
              <motion.form
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={saveCourse}
                className="card-dark p-7 sm:p-8 space-y-5"
                data-testid="admin-course-form"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-black text-xl text-[#0B1B3A]">
                    {courseForm.editingId ? t("admin.editCourse") : t("admin.newCourse")}
                  </h3>
                  <button type="button" data-testid="admin-course-cancel" onClick={() => setCourseForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#1D4ED8] transition-colors duration-200">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div>
                  <Label>{t("admin.field.title")}</Label>
                  <input data-testid="admin-course-title-input" required value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} className="input-flat mt-2" />
                </div>
                <div>
                  <Label>{t("admin.field.description")}</Label>
                  <textarea data-testid="admin-course-description-input" rows={3} value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} className="input-flat mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>{t("admin.field.category")}</Label>
                    <input data-testid="admin-course-category-input" value={courseForm.category} onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.level")}</Label>
                    <select data-testid="admin-course-level-select" value={courseForm.level} onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })} className="input-flat mt-2">
                      <option value="Beginner">{t("level.Beginner")}</option>
                      <option value="Intermediate">{t("level.Intermediate")}</option>
                      <option value="Advanced">{t("level.Advanced")}</option>
                    </select>
                  </div>
                  <div>
                    <Label>{t("admin.field.price")}</Label>
                    <input data-testid="admin-course-price-input" type="number" min={0} value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.months")}</Label>
                    <input data-testid="admin-course-months-input" type="number" min={1} value={courseForm.duration_months} onChange={(e) => setCourseForm({ ...courseForm, duration_months: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.start")}</Label>
                    <input data-testid="admin-course-start-input" type="date" value={courseForm.start_date} onChange={(e) => setCourseForm({ ...courseForm, start_date: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.deposit")}</Label>
                    <input data-testid="admin-course-deposit-input" type="number" min={0} value={courseForm.deposit} onChange={(e) => setCourseForm({ ...courseForm, deposit: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.installments")}</Label>
                    <input data-testid="admin-course-installments-input" type="number" min={0} value={courseForm.installments} onChange={(e) => setCourseForm({ ...courseForm, installments: e.target.value })} className="input-flat mt-2" />
                  </div>
                  <div>
                    <Label>{t("admin.field.code")}</Label>
                    <input data-testid="admin-course-code-input" value={courseForm.access_code} onChange={(e) => setCourseForm({ ...courseForm, access_code: e.target.value.toUpperCase() })} className="input-flat mt-2 uppercase tracking-widest" placeholder="FOOTBALL26" />
                  </div>
                </div>
                <div>
                  <Label>{t("admin.field.schedule")}</Label>
                  <input data-testid="admin-course-schedule-input" value={courseForm.schedule} onChange={(e) => setCourseForm({ ...courseForm, schedule: e.target.value })} className="input-flat mt-2" placeholder="Τρίτη & Πέμπτη 19:00 - 21:00" />
                </div>
                <div>
                  <Label>{t("admin.field.thumbnail")}</Label>
                  <input data-testid="admin-course-thumbnail-input" value={courseForm.thumbnail} onChange={(e) => setCourseForm({ ...courseForm, thumbnail: e.target.value })} className="input-flat mt-2" placeholder="https://..." />
                </div>
                <button data-testid="admin-course-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
              </motion.form>
            )}

            {selected && !courseForm && (
              <div>
                <div className="card-cream p-7 sm:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading font-black text-2xl text-[#0B1B3A]" data-testid="admin-selected-course-title">{selected.course.title}</h3>
                      <p className="text-sm text-[#475569] mt-2 max-w-xl">{selected.course.description}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs font-bold uppercase tracking-[0.14em] text-[#475569]">
                        {selected.course.price ? <span>{selected.course.price}€</span> : null}
                        {selected.course.duration_months ? <span>{selected.course.duration_months} {t("program.months")}</span> : null}
                        {selected.course.start_date && <span>{t("program.start")}: {selected.course.start_date}</span>}
                        {selected.course.access_code && (
                          <span className="inline-flex items-center gap-1 bg-[#1D4ED8] text-white px-3 py-1 rounded-full" data-testid="admin-course-code-badge">
                            <KeyRound className="w-3 h-3" /> {selected.course.access_code}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid="admin-edit-course-button"
                        onClick={() => setCourseForm({
                          editingId: selected.course.id,
                          title: selected.course.title,
                          description: selected.course.description,
                          category: selected.course.category,
                          level: selected.course.level,
                          thumbnail: selected.course.thumbnail,
                          price: selected.course.price || 0,
                          duration_months: selected.course.duration_months || 7,
                          start_date: selected.course.start_date || "",
                          access_code: selected.course.access_code || "",
                          schedule: selected.course.schedule || "",
                          deposit: selected.course.deposit || 0,
                          installments: selected.course.installments || 0,
                        })}
                        className="btn-ghost-dark !px-5 !py-2 text-xs"
                      >
                        <Pencil className="w-4 h-4" /> {t("admin.edit")}
                      </button>
                      <button
                        data-testid="admin-delete-course-button"
                        onClick={() => deleteCourse(selected.course.id)}
                        className="inline-flex items-center gap-2 bg-[#EF4444] text-white font-bold px-5 py-2 rounded-full text-xs hover:bg-[#EEF4FF] transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" /> {t("admin.delete")}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 mt-10">
                  <h3 className="font-heading font-black text-xl text-[#0B1B3A]">{t("admin.lessons")}</h3>
                  <button
                    data-testid="admin-new-lesson-button"
                    onClick={() => setLessonForm({ editingId: null, ...emptyLessonForm, quiz: [emptyQuestion()] })}
                    className="btn-blue !px-5 !py-2 text-xs"
                  >
                    <Plus className="w-4 h-4" /> {t("admin.newLesson")}
                  </button>
                </div>

                <div className="mt-6 card-dark overflow-hidden divide-y divide-[#E2E8F0]">
                  {selected.lessons.map((l, i) => (
                    <div key={l.id} className="flex items-center gap-4 p-4" data-testid={`admin-lesson-item-${l.id}`}>
                      <span className="font-heading font-black text-lg text-[#94A3B8] w-8">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold truncate text-[#0B1B3A]">{l.title}</p>
                        <p className="text-xs text-[#64748B] flex flex-wrap items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <ClipboardList className="w-3 h-3" /> {(l.quiz || []).length} {t("course.quizCount")}
                          </span>
                          <span>{t("program.week")} {l.unlock_week || 1}</span>
                        </p>
                      </div>
                      <button
                        data-testid={`admin-edit-lesson-${l.id}`}
                        onClick={() =>
                          setLessonForm({
                            editingId: l.id,
                            title: l.title,
                            content: l.content,
                            video_url: l.video_url || "",
                            unlock_week: l.unlock_week || 1,
                            quiz: (l.quiz || []).map((q) => ({ question: q.question, options: [...q.options], correct_index: q.correct_index })),
                          })
                        }
                        className="p-2 rounded-full text-[#334155] hover:bg-[#1D4ED8] hover:text-white transition-colors duration-200"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-delete-lesson-${l.id}`}
                        onClick={() => deleteLesson(l.id)}
                        className="p-2 rounded-full text-[#334155] hover:bg-[#EF4444] hover:text-[#1D4ED8] transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {selected.lessons.length === 0 && <p className="p-4 text-sm text-[#94A3B8]">-</p>}
                </div>

                {lessonForm && (
                  <motion.form
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    onSubmit={saveLesson}
                    className="card-dark p-7 sm:p-8 mt-8 space-y-5"
                    data-testid="admin-lesson-form"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-heading font-black text-lg text-[#0B1B3A]">
                        {lessonForm.editingId ? t("admin.editLesson") : t("admin.newLesson")}
                      </h4>
                      <button type="button" data-testid="admin-lesson-cancel" onClick={() => setLessonForm(null)} className="p-2 rounded-full text-[#475569] hover:bg-[#F1F5F9] hover:text-[#1D4ED8] transition-colors duration-200">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="md:col-span-2">
                        <Label>{t("admin.field.title")}</Label>
                        <input data-testid="admin-lesson-title-input" required value={lessonForm.title} onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })} className="input-flat mt-2" />
                      </div>
                      <div>
                        <Label>{t("admin.field.week")}</Label>
                        <input data-testid="admin-lesson-week-input" type="number" min={1} max={40} value={lessonForm.unlock_week} onChange={(e) => setLessonForm({ ...lessonForm, unlock_week: e.target.value })} className="input-flat mt-2" />
                      </div>
                    </div>
                    <div>
                      <Label>{t("admin.field.content")}</Label>
                      <textarea data-testid="admin-lesson-content-input" rows={6} value={lessonForm.content} onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })} className="input-flat mt-2" />
                    </div>
                    <div>
                      <Label>{t("admin.field.video")}</Label>
                      <input data-testid="admin-lesson-video-input" value={lessonForm.video_url} onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })} className="input-flat mt-2" placeholder="https://youtube.com/watch?v=..." />
                    </div>

                    <div className="border-t border-[#E2E8F0] pt-6">
                      <div className="flex items-center justify-between gap-3">
                        <h5 className="font-heading font-black text-base text-[#0B1B3A]">{t("admin.quiz")}</h5>
                        <button
                          type="button"
                          data-testid="admin-add-question-button"
                          onClick={() => setLessonForm({ ...lessonForm, quiz: [...lessonForm.quiz, emptyQuestion()] })}
                          className="btn-outline-light !px-5 !py-2 text-xs"
                        >
                          <Plus className="w-4 h-4" /> {t("admin.addQuestion")}
                        </button>
                      </div>

                      <div className="space-y-5 mt-6">
                        {lessonForm.quiz.map((q, qi) => (
                          <div key={qi} className="rounded-[20px] border border-[#E2E8F0] bg-[#F8FAFC] p-5" data-testid={`admin-question-${qi}`}>
                            <div className="flex items-center justify-between">
                              <Label>{t("admin.question")} {qi + 1}</Label>
                              <button
                                type="button"
                                data-testid={`admin-remove-question-${qi}`}
                                onClick={() => setLessonForm({ ...lessonForm, quiz: lessonForm.quiz.filter((_, i) => i !== qi) })}
                                className="text-xs font-bold text-[#DC2626] hover:underline"
                              >
                                {t("admin.removeQuestion")}
                              </button>
                            </div>
                            <input data-testid={`admin-question-text-${qi}`} value={q.question} onChange={(e) => updateQuestion(qi, { question: e.target.value })} className="input-flat mt-2" placeholder={t("admin.question")} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                              {q.options.map((opt, oi) => (
                                <input key={oi} data-testid={`admin-option-${qi}-${oi}`} value={opt} onChange={(e) => updateOption(qi, oi, e.target.value)} className="input-flat" placeholder={`${t("admin.options")} ${oi + 1}`} />
                              ))}
                            </div>
                            <div className="mt-4">
                              <Label>{t("admin.correct")}</Label>
                              <select data-testid={`admin-correct-select-${qi}`} value={q.correct_index} onChange={(e) => updateQuestion(qi, { correct_index: Number(e.target.value) })} className="input-flat mt-2 md:w-64">
                                {q.options.map((_, oi) => (
                                  <option key={oi} value={oi}>{t("admin.options")} {oi + 1}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button data-testid="admin-lesson-save-button" type="submit" className="btn-blue">{t("admin.save")}</button>
                  </motion.form>
                )}
              </div>
            )}

            {!selected && !courseForm && (
              <div className="card-dark p-16 text-center text-[#64748B]" data-testid="admin-empty-state">
                {t("admin.selectCourse")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
